import { useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

/**
 * Generates a stable browser fingerprint hash (SHA-256)
 * Based on: user agent, screen resolution, timezone, platform, color depth
 */
async function generateDeviceFingerprint(): Promise<string> {
  const components = [
    navigator.userAgent,
    `${screen.width}x${screen.height}x${screen.colorDepth}`,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.platform,
    navigator.language,
  ].join("|");

  const encoder = new TextEncoder();
  const data = encoder.encode(components);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Generates a human-readable device label
 */
function getDeviceLabel(): string {
  const ua = navigator.userAgent;
  let browser = "Navigateur";
  let os = "Inconnu";

  if (ua.includes("Chrome")) browser = "Chrome";
  else if (ua.includes("Firefox")) browser = "Firefox";
  else if (ua.includes("Safari")) browser = "Safari";
  else if (ua.includes("Edge")) browser = "Edge";

  if (ua.includes("Windows")) os = "Windows";
  else if (ua.includes("Mac")) os = "macOS";
  else if (ua.includes("Linux")) os = "Linux";
  else if (ua.includes("Android")) os = "Android";
  else if (ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";

  return `${browser} – ${os}`;
}

export function useDeviceCheck() {
  const { user } = useAuth();

  const checkAndRegisterDevice = useCallback(async () => {
    if (!user) return;

    try {
      const fingerprint = await generateDeviceFingerprint();
      const label = getDeviceLabel();

      // Upsert the device (update last_seen_at if already registered)
      const { error } = await supabase
        .from("user_devices")
        .upsert(
          {
            user_id: user.id,
            device_fingerprint: fingerprint,
            device_label: label,
            last_seen_at: new Date().toISOString(),
            is_active: true,
          },
          { onConflict: "user_id,device_fingerprint" }
        );

      if (error) {
        console.error("Device registration error:", error);
      }
    } catch (err) {
      console.error("Device fingerprint error:", err);
    }
  }, [user]);

  useEffect(() => {
    checkAndRegisterDevice();
  }, [checkAndRegisterDevice]);

  return { checkAndRegisterDevice };
}
